/**
 * Component Registry for Z Modular Theme
 * 
 * Extends the base avenue theme registry with Z Modular specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-avenue/registry';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { AmenitiesHero } from './components/AmenitiesHero';
import { AmenitiesList } from './components/AmenitiesList';
import { ContactHero } from './components/ContactHero';
import { Gallery } from './components/Gallery';
import { GalleryHero } from './components/GalleryHero';
import { NeighborhoodHero } from './components/NeighborhoodHero';
import { VirtualTourHero } from './components/VirtualTourHero';
import { FloorplansList } from './components/FloorplansList';
import { CTASection } from './components/CTASection';
import { BetterBuiltBenefits } from './components/BetterBuiltBenefits';
import { BetterBuiltCTA } from './components/BetterBuiltCTA';
import { BetterBuiltFeatures } from './components/BetterBuiltFeatures';
import { BetterBuiltIntro } from './components/BetterBuiltIntro';
import { BetterBuiltSteelVsWood } from './components/BetterBuiltSteelVsWood';
import { FlexibleLivingPrograms } from './components/FlexibleLivingPrograms';
import { FloorPlansHero } from './components/FloorPlansHero';
import { LifestyleCommunity } from './components/LifestyleCommunity';
import { LifestyleCTA } from './components/LifestyleCTA';
import { LifestyleFeatures } from './components/LifestyleFeatures';
import { LifestyleHero } from './components/LifestyleHero';
import { PageHero } from './components/PageHero';
import { PropertyOverview } from './components/PropertyOverview';
import { Reviews } from './components/Reviews';
import { ReviewsCallout } from './components/ReviewsCallout';
import { RewardsPrograms } from './components/RewardsPrograms';
import { SectionDivider } from './components/SectionDivider';
import { SitemapContent } from './components/SitemapContent';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { Navigation } from './components/Navigation';
import { ContactForm } from './components/ContactForm';
import { ScheduleTourForm } from './components/ScheduleTourForm';
import { SocialFollow } from './components/SocialFollow';
import { ThankYouContent } from './components/ThankYouContent';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { PetPolicy } from './components/PetPolicy';
import { ResidentAccess } from './components/ResidentAccess';
import { ApplyNow } from './components/ApplyNow';
import { TeamMembers } from './components/TeamMembers';
import { TeamCTA } from './components/TeamCTA';
import { FAQList } from './components/FAQList';
import { InteractiveMapSection } from './components/InteractiveMapSection';

/**
 * Z Modular specific component overrides.
 * These components have been customized for the Z Modular brand.
 */
const zmodularOverrides: Partial<ComponentRegistry> = {
  Footer,
  Header,
  Hero,
  AmenitiesHero,
  AmenitiesList,
  ContactHero,
  Gallery,
  GalleryHero,
  NeighborhoodHero,
  VirtualTourHero,
  FloorplansList,
  CTASection,
  BetterBuiltBenefits,
  BetterBuiltCTA,
  BetterBuiltFeatures,
  BetterBuiltIntro,
  BetterBuiltSteelVsWood,
  FlexibleLivingPrograms,
  FloorPlansHero,
  LifestyleCommunity,
  LifestyleCTA,
  LifestyleFeatures,
  LifestyleHero,
  PageHero,
  PropertyOverview,
  Reviews,
  ReviewsCallout,
  RewardsPrograms,
  SectionDivider,
  SitemapContent,
  VirtualTourEmbed,
  AccessibilityStatement,
  Navigation,
  ContactForm,
  ScheduleTourForm,
  SocialFollow,
  ThankYouContent,
  NeighborhoodMap,
  PetPolicy,
  ResidentAccess,
  ApplyNow,
  TeamMembers,
  TeamCTA,
  FAQList,
  InteractiveMapSection,
};

/**
 * Final merged registry for Z Modular properties.
 * Inherits all components from the avenue theme, with Z Modular overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...zmodularOverrides,
};

export default registry;
